import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';

import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type CellValueChangedEvent,
  type ColDef,
  type ColumnMovedEvent,
  type ColumnResizedEvent,
  type GridApi,
  type GridReadyEvent,
} from 'ag-grid-community';

import { workflowService } from '../services/workflowService';
import { CsvMatrix, normalizeCsvText, parseCsv, serializeCsv, shouldUseRawCsvMode } from '../utils/csvArtifacts';

ModuleRegistry.registerModules([AllCommunityModule]);

async function blobToText(blob: Blob): Promise<string> {
  return await blob.text();
}

function isRowNumberHeaderLabel(label: string | undefined | null): boolean {
  const v = (label || '').trim().toLowerCase();
  return v === '#' || v === 'no' || v === 'no.' || v === 's.no' || v === 's.no.' || v === 'sr#';
}

function renumberFirstColumnIfRowNumbers(matrix: CsvMatrix): CsvMatrix {
  const header = matrix[0] ?? [];
  if (!isRowNumberHeaderLabel(header[0] ?? '')) return matrix;
  // Clone only what we need; keep header row reference stable if possible.
  const next: CsvMatrix = [header];
  for (let i = 1; i < matrix.length; i++) {
    const row = matrix[i] ?? [];
    const cloned = [...row];
    cloned[0] = `#${i}`;
    next.push(cloned);
  }
  return next;
}

export default function CsvArtifactEditorPage() {
  const [searchParams] = useSearchParams();
  const stepId = searchParams.get('stepId') || '';
  const artifactKey = searchParams.get('artifactKey') || '';
  const title = searchParams.get('title') || 'Sheet editor';

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [mode, setMode] = useState<'grid' | 'raw'>('raw');
  const [delimiter, setDelimiter] = useState(',');
  const [rows, setRows] = useState<CsvMatrix>([['']]);
  const [rawText, setRawText] = useState('');
  const [autoExpandRows, setAutoExpandRows] = useState(true);
  const [wrapCells, setWrapCells] = useState(true);
  const gridApiRef = useRef<GridApi | null>(null);
  const [quickFilter, setQuickFilter] = useState('');
  const [colWidthByField, setColWidthByField] = useState<Record<string, number>>({});
  const [colOrder, setColOrder] = useState<string[]>([]);

  const rowCount = rows.length;
  const colCount = rows[0]?.length ?? 0;
  const isTooLargeForGrid = useMemo(() => shouldUseRawCsvMode(rowCount, colCount), [rowCount, colCount]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!stepId || !artifactKey) return;
      setIsLoading(true);
      setError(null);
      setInfo(null);
      try {
        const blob = await workflowService.downloadStepArtifact(stepId, artifactKey);
        const text = await blobToText(blob);
        if (cancelled) return;
        setRawText(text || '');
        const parsed = parseCsv(text || '');
        setDelimiter(parsed.delimiter);
        const loaded = parsed.rows.length ? parsed.rows : [['']];
        setRows(renumberFirstColumnIfRowNumbers(loaded));
        const nextMode = shouldUseRawCsvMode(parsed.rows.length || 1, parsed.rows[0]?.length ?? 1) ? 'raw' : 'grid';
        setMode(nextMode);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load CSV');
        setMode('raw');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [stepId, artifactKey]);

  const addRow = () => {
    setRows((prev) => {
      const cols = prev[0]?.length ?? 1;
      const next = [...prev, Array(cols).fill('')];
      // If first column is row numbers, keep it sequential.
      if (isRowNumberHeaderLabel((prev[0]?.[0] ?? '') as any)) {
        next[next.length - 1][0] = `#${next.length - 1}`;
      }
      return next;
    });
  };

  const addColumn = () => {
    setRows((prev) => prev.map((r) => [...r, '']));
  };

  const handleDownload = async () => {
    try {
      const blob = await workflowService.downloadStepArtifact(stepId, artifactKey);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = artifactKey.endsWith('_csv')
        ? `${artifactKey.replace(/_csv$/i, '').replace(/_/g, '-')}.csv`
        : artifactKey.endsWith('_xlsx')
          ? `${artifactKey.replace(/_xlsx$/i, '').replace(/_/g, '-')}.xlsx`
          : artifactKey;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download.');
    }
  };

  const handleSave = async () => {
    if (!stepId || !artifactKey) return;
    setIsSaving(true);
    setError(null);
    setInfo(null);
    try {
      const csv = mode === 'raw' ? normalizeCsvText(rawText || '') : serializeCsv(rows, delimiter);
      await workflowService.updateStepArtifact(stepId, artifactKey, csv);
      setInfo('Saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  };

  const header = useMemo(() => rows[0] ?? [], [rows]);
  const dataRows = useMemo(() => rows.slice(1), [rows]);

  const canUseGrid = artifactKey.toLowerCase().endsWith('_csv') && !isTooLargeForGrid;

  type AgRow = Record<string, string> & { __id: string };

  const widthFor = (labelRaw: string, idx: number): { width?: number; minWidth?: number } => {
    const label = (labelRaw || '').trim().toLowerCase();
    if (label === '#' || label === 'no.' || label === 'sr#' || idx === 0) return { width: 72, minWidth: 56 };
    if (label.includes('requirement id')) return { width: 140, minWidth: 120 };
    if (label === 'clause' || label.includes('clause')) return { width: 90, minWidth: 80 };
    if (label === 'standards' || label.includes('standard')) return { width: 160, minWidth: 130 };
    if (label === 'title') return { width: 240, minWidth: 180 };
    if (label === 'requirement') return { width: 420, minWidth: 240 };
    if (label.includes('applicable')) return { width: 170, minWidth: 150 };
    if (label.includes('comment')) return { width: 420, minWidth: 240 };
    if (label === 'sop' || label.includes('format') || label.includes('process') || label.includes('function')) return { width: 220, minWidth: 160 };
    return { width: 180, minWidth: 140 };
  };

  const shouldAutoHeightForColumn = (labelRaw: string): boolean => {
    const label = (labelRaw || '').trim().toLowerCase();
    if (label === 'requirement') return true;
    if (label.includes('comment') || label.includes('discussion') || label.includes('justification') || label.includes('evidence')) return true;
    return false;
  };

  const columnDefs: ColDef<AgRow>[] = useMemo(() => {
    const rowNumberCol = isRowNumberHeaderLabel(header[0] ?? '');
    const fields = header.map((_, idx) => `c${idx}`);
    const orderedFields = colOrder.length
      ? [...fields].sort((a, b) => {
          const ia = colOrder.indexOf(a);
          const ib = colOrder.indexOf(b);
          if (ia === -1 && ib === -1) return a.localeCompare(b);
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        })
      : fields;

    return orderedFields.map((field) => {
      const idx = Number(field.replace(/^c/, ''));
      const labelRaw = header[idx] ?? '';
      const label = (labelRaw || `Column ${idx + 1}`).trim() || `Column ${idx + 1}`;
      const { width: defaultWidth, minWidth } = widthFor(label, idx);
      const width = colWidthByField[field] ?? defaultWidth;
      const autoHeight = Boolean(wrapCells && autoExpandRows && shouldAutoHeightForColumn(label));
      return {
        field,
        headerName: label,
        editable: !(rowNumberCol && idx === 0),
        resizable: true,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        width,
        minWidth,
        pinned: rowNumberCol && idx === 0 ? 'left' : undefined,
        suppressMovable: rowNumberCol && idx === 0 ? true : undefined,
        wrapText: wrapCells,
        autoHeight,
        cellStyle: wrapCells
          ? () => ({
              whiteSpace: 'normal',
              lineHeight: '1.25',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            })
          : undefined,
      };
    });
  }, [header, wrapCells, autoExpandRows, colWidthByField, colOrder]);

  const rowData: AgRow[] = useMemo(() => {
    const cols = header.length;
    return dataRows.map((r, idx) => {
      const row: AgRow = { __id: `${idx}` };
      for (let c = 0; c < cols; c++) row[`c${c}`] = `${r?.[c] ?? ''}`;
      return row;
    });
  }, [dataRows, header.length]);

  useEffect(() => {
    // If wrapping/auto-height toggles changed, ensure AG Grid recomputes row heights.
    try {
      gridApiRef.current?.resetRowHeights();
    } catch {
      // ignore
    }
  }, [wrapCells, autoExpandRows, header.length]);

  const onGridReady = (e: GridReadyEvent<AgRow>) => {
    gridApiRef.current = e.api;
    try {
      const next = e.api.getColumnDefs()?.map((d: any) => `${d?.field ?? ''}`).filter(Boolean) as string[];
      if (next?.length) setColOrder(next);
    } catch {
      // ignore
    }
  };

  const onCellValueChanged = (e: CellValueChangedEvent<AgRow>) => {
    const field = `${e.colDef.field || ''}`;
    const match = field.match(/^c(\d+)$/);
    if (!match) return;
    const colIdx = Number(match[1]);
    const rowIdx = (e.node?.rowIndex ?? 0) + 1; // +1 for header row in CsvMatrix
    const nextValue = `${e.newValue ?? ''}`;
    setRows((prev) => {
      // IMPORTANT: keep header row reference stable so columnDefs don't rebuild on every edit.
      const next = prev.map((r, i) => (i === 0 ? r : [...r]));
      while (next.length <= rowIdx) next.push(Array(next[0]?.length ?? 1).fill(''));
      while ((next[rowIdx]?.length ?? 0) <= colIdx) next[rowIdx].push('');
      next[rowIdx][colIdx] = nextValue;
      return next;
    });
  };

  const onColumnResized = (e: ColumnResizedEvent<AgRow>) => {
    if (!e.finished) return;
    const colId = e.column?.getColId?.();
    if (!colId) return;
    const w = e.column?.getActualWidth?.();
    if (!w) return;
    setColWidthByField((prev) => ({ ...prev, [colId]: w }));
  };

  const onColumnMoved = (e: ColumnMovedEvent<AgRow>) => {
    try {
      const fields = e.api.getAllGridColumns().map((c) => c.getColId()).filter(Boolean);
      setColOrder(fields);
    } catch {
      // ignore
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f6f7f9' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#ffffff', color: '#111827', borderBottom: '1px solid #e5e7eb' }}>
        <Toolbar sx={{ gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }} noWrap>
              {artifactKey} • {stepId}
            </Typography>
          </Box>

          <Button
            size="small"
            variant={mode === 'raw' ? 'contained' : 'outlined'}
            onClick={() => {
              try {
                if (mode === 'grid') {
                  setRawText(serializeCsv(rows, delimiter));
                }
              } catch {
                // ignore
              }
              setMode('raw');
            }}
            disabled={isSaving}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Raw CSV
          </Button>
          <Button
            size="small"
            variant={mode === 'grid' ? 'contained' : 'outlined'}
            onClick={() => {
              if (!canUseGrid) return;
              try {
                const parsed = parseCsv(rawText || '');
                setDelimiter(parsed.delimiter);
                setRows(parsed.rows.length ? parsed.rows : [['']]);
                setMode('grid');
              } catch {
                setError('Failed to parse CSV for grid view.');
              }
            }}
            disabled={isSaving || !canUseGrid}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Grid
          </Button>

          <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />

          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={isLoading || isSaving || !stepId || !artifactKey}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Download
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isLoading || isSaving || !stepId || !artifactKey}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
          <IconButton onClick={() => window.close()} size="small" sx={{ ml: 0.5 }} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2 }}>
        {!stepId || !artifactKey ? (
          <Alert severity="error">Missing required parameters: stepId and artifactKey.</Alert>
        ) : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {info ? <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert> : null}

        <Box sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 2, p: { xs: 1.25, md: 2 } }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  Rows: <b>{rowCount}</b> • Columns: <b>{colCount}</b>
                </Typography>
                {mode === 'grid' && (
                  <>
                    <Divider flexItem orientation="vertical" />
                    <Button size="small" variant="outlined" onClick={addRow} disabled={isSaving} sx={{ textTransform: 'none' }}>
                      Add row
                    </Button>
                    <Button size="small" variant="outlined" onClick={addColumn} disabled={isSaving} sx={{ textTransform: 'none' }}>
                      Add column
                    </Button>
                    <Button
                      size="small"
                      variant={autoExpandRows ? 'contained' : 'outlined'}
                      onClick={() => setAutoExpandRows((v) => !v)}
                      disabled={isSaving}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Auto-expand rows
                    </Button>
                    <Button
                      size="small"
                      variant={wrapCells ? 'contained' : 'outlined'}
                      onClick={() => setWrapCells((v) => !v)}
                      disabled={isSaving}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Wrap cells
                    </Button>
                    <TextField
                      size="small"
                      label="Search"
                      value={quickFilter}
                      onChange={(e) => setQuickFilter(e.target.value)}
                      sx={{ width: 220 }}
                      disabled={isSaving}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        try {
                          gridApiRef.current?.sizeColumnsToFit();
                        } catch {
                          // ignore
                        }
                      }}
                      disabled={isSaving}
                      sx={{ textTransform: 'none' }}
                    >
                      Fit columns
                    </Button>
                    <TextField
                      size="small"
                      label="Delimiter"
                      value={delimiter}
                      onChange={(e) => setDelimiter(e.target.value || ',')}
                      inputProps={{ maxLength: 1 }}
                      sx={{ width: 120 }}
                      disabled={isSaving}
                    />
                  </>
                )}
              </Box>

              {!canUseGrid && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Grid view is disabled for large CSVs to keep the editor fast and stable. Use Raw CSV mode.
                </Alert>
              )}

              {mode === 'raw' ? (
                <TextField
                  label="CSV"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={22}
                  maxRows={44}
                  disabled={isSaving}
                  sx={{
                    '& textarea': {
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: '0.85rem',
                      lineHeight: 1.45,
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    height: '70vh',
                    bgcolor: '#ffffff',
                  }}
                >
                  <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                    <AgGridReact<AgRow>
                      onGridReady={onGridReady}
                      rowData={rowData}
                      columnDefs={columnDefs}
                      getRowId={(p) => `${p.data.__id}`}
                      onCellValueChanged={onCellValueChanged}
                      onColumnResized={onColumnResized}
                      onColumnMoved={onColumnMoved}
                      quickFilterText={quickFilter}
                      singleClickEdit={true}
                      stopEditingWhenCellsLoseFocus={true}
                      enterNavigatesVertically={true}
                      enterNavigatesVerticallyAfterEdit={true}
                      undoRedoCellEditing={true}
                      enableCellTextSelection={true}
                      ensureDomOrder={true}
                      suppressRowTransform={Boolean(wrapCells && autoExpandRows)}
                      defaultColDef={{
                        editable: true,
                        resizable: true,
                        sortable: false,
                        filter: false,
                        suppressHeaderMenuButton: true,
                      }}
                      headerHeight={38}
                      rowHeight={34}
                      suppressDragLeaveHidesColumns={true}
                    />
                  </div>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

